'use client';

import { Alert } from '@mui/material';
import { InterviewRoundType, ScreeningInterviewStatus } from '@prisma/client/browser';
import axios from 'axios';
import { useToast } from '@/app/context/toast-provider';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '@/app/utils/errorMessage';
import { useConfirmDialog } from '../useConfirmDialog/useConfirmDialog';
/**
 * Removing rounds, panelists and the phone screening from an interview. Drafts (`id` 0) only exist in the browser
 * and are dropped without a request. Persisted rounds are confirmed first: a screening round that already holds
 * assessment data gets the stronger warning. Removing a round's last panelist removes the round.
 */
export function useInterviewRoundDeletion({
  interview,
  setInterview,
  emailRef,
  refreshJobApplicationList,
  setRequestLoading,
  readOnly = false
}) {
  const {
    confirm
  } = useConfirmDialog();
  const {
    showErrorToast,
    showSuccessToast
  } = useToast();
  const deleteInterviewRound = async interviewRoundId => {
    try {
      if (interviewRoundId === 0) {
        setInterview({
          ...interview,
          InterviewRounds: interview.InterviewRounds.filter(round => round.id !== 0)
        });
        return;
      }
      setRequestLoading?.(true);
      const interviewResp = await axios.delete(`/api/interviews/${interview.id}/interviewRound/${interviewRoundId}`);
      if (interviewResp.status === 200) {
        showSuccessToast(SUCCESS_MESSAGES.INTERVIEW_ROUND_DELETED_SUCCESS);
        // DELETE returns raw InterviewDBQuery data and omits GET enrichments
        // (assessmentTemplateId / hasAiPhoneScreeningCall). Preserve them so
        // Add AI Interview / Phone Screening menu options stay visible.
        setInterview({
          ...interviewResp.data.data,
          email: interview?.email || emailRef?.current,
          assessmentTemplateId: interview?.assessmentTemplateId,
          hasAIScreeningInterview: interview?.hasAIScreeningInterview,
          hasAiPhoneScreeningCall: interview?.hasAiPhoneScreeningCall,
          firstName: interview?.firstName,
          lastName: interview?.lastName,
          jobTitle: interview?.jobTitle,
          jobPostingId: interview?.jobPostingId,
          departmentId: interview?.departmentId
        });
        refreshJobApplicationList?.();
        setRequestLoading?.(false);
      }
    } catch (e) {
      showErrorToast(ERROR_MESSAGES.INTERVIEW_ROUND_DELETED_FAIL);
      setRequestLoading?.(false);
      throw e;
    }
  };
  const deletePanellist = async (panellist, ir) => {
    const withoutPanelist = prevInterview => ({
      ...prevInterview,
      InterviewRounds: prevInterview.InterviewRounds.map(round => round.id === ir.id ? {
        ...round,
        PanellistFeedback: round.PanellistFeedback?.filter(p => p.email !== panellist.email)
      } : round)
    });
    try {
      if (ir.id === 0) {
        setInterview(withoutPanelist);
        return;
      }

      // The last panelist takes the round with them. No nested confirm: callers confirm before calling this.
      if (ir.PanellistFeedback.length === 1) {
        await deleteInterviewRound(ir.id);
        return;
      }
      setRequestLoading?.(true);
      const res = await axios.delete(`/api/interviews/${interview?.id}/interviewRound/${ir.id}/panellist?email=${encodeURIComponent(panellist.email)}`);
      if (res.status === 200) {
        setInterview(withoutPanelist);
        showSuccessToast(SUCCESS_MESSAGES.PANELIST_DELETED_SUCCESS);
      }
      setRequestLoading?.(false);
    } catch {
      showErrorToast(ERROR_MESSAGES.PANELIST_DELETED_FAIL);
      setRequestLoading?.(false);
    }
  };

  /** A screening round the candidate has started or finished: deleting it loses the report, scores and recording. */
  const holdsAssessmentData = interviewRound => interviewRound?.roundType === InterviewRoundType.SCREENING && interview?.ScreeningInterview?.status && interview?.ScreeningInterview?.status !== ScreeningInterviewStatus.INIT && interview?.ScreeningInterview?.status !== ScreeningInterviewStatus.QUESTIONS_GENERATED;

  /** The user-facing delete: drafts go at once, persisted rounds are confirmed with the warning that fits. */
  const handleDeleteInterviewRound = interviewRoundId => {
    if (readOnly) return;
    if (interviewRoundId === 0) {
      deleteInterviewRound(interviewRoundId);
      return;
    }
    const interviewRound = interview?.InterviewRounds?.find(ir => ir.id === interviewRoundId);
    if (holdsAssessmentData(interviewRound)) {
      confirm({
        title: 'Delete Screening Interview',
        color: 'error',
        confirmButtonText: 'Delete Interview',
        content: <div className="flex flex-col gap-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <div className="text-red-600 text-xl -mt-1!">⚠️</div>
                                <div>
                                    <p className="text-sm font-semibold text-red-800 mb-2">This interview contains candidate assessment data</p>
                                    <p className="text-sm text-red-700">
                                        If you continue, you will lose any interview report, assessment scores, recording, and other data associated with this
                                        interview.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="text-center">
                            <p className="text-sm text-foreground mb-2">Are you sure you want to delete this screening interview?</p>
                            <p className="text-xs text-foreground-muted">This action cannot be undone. An email notification will be sent to the candidate.</p>
                        </div>
                    </div>,
        onConfirm: () => deleteInterviewRound(interviewRoundId)
      });
    } else {
      confirm({
        title: 'Delete Interview Round',
        color: 'error',
        confirmButtonText: 'Delete Interview Round',
        content: <div className="flex flex-col gap-3">
                        <p className="text-sm font-semibold text-black">Are you sure you want to delete this interview round?</p>
                        <p className="text-sm text-foreground-muted">
                            This action will permanently remove the interview round and all associated data. This cannot be undone.
                        </p>
                        <p className="text-sm text-orange-600 font-medium">Note: An email will be sent to the candidate and all participants to notify them.</p>
                    </div>,
        onConfirm: () => deleteInterviewRound(interviewRoundId)
      });
    }
  };

  /** Before removing a booked round because its last panelist is going: the candidate and panel are emailed. */
  const confirmRemoveInterviewRound = onConfirm => {
    confirm({
      title: 'Confirm Removing Interview Round',
      content: <>
                    <p className="text-sm font-medium text-foreground">Are you sure you want to remove this interview round?</p>
                    <Alert severity="warning" variant="standard" className="mt-2">
                        An email will be sent to the candidate and all the participants to notify them.
                    </Alert>
                </>,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Remove',
      color: 'error',
      onConfirm
    });
  };
  const confirmRemovePanellist = (panelist, onConfirm) => {
    confirm({
      title: 'Remove Panelist',
      content: <p className="text-sm font-medium text-foreground">
                    Are you sure you want to remove <strong>{panelist?.name || panelist?.email || 'this panelist'}</strong> from this interview round?
                </p>,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Remove',
      color: 'error',
      onConfirm
    });
  };

  /** Removes a panelist from a booked round, warning that the round goes too when they are the last one. */
  const handleRemovePanellist = (panelist, ir) => {
    if (readOnly) return;
    if (!ir?.eventId) {
      if (ir?.PanellistFeedback?.length === 1) deleteInterviewRound(ir.id);else deletePanellist(panelist, ir);
      return;
    }
    if (ir.PanellistFeedback?.length === 1) confirmRemoveInterviewRound(() => deletePanellist(panelist, ir));else confirmRemovePanellist(panelist, () => deletePanellist(panelist, ir));
  };

  /** The phone screening isn't an InterviewRound; it has its own endpoint and no email goes out. */
  const handleDeletePhoneScreening = () => {
    const screeningCallId = interview?.ScreeningCall?.id;
    if (!screeningCallId || readOnly) return;
    confirm({
      variant: 'confirm',
      title: 'Delete Phone Screening Call',
      content: 'Are you sure you want to delete this scheduled phone screening call? This action cannot be undone.',
      color: 'error',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await axios.delete(`/api/interviews/screening-call?id=${screeningCallId}`);
          if (response.status === 200 || response.data?.success) {
            showSuccessToast('Phone call deleted successfully');
            setInterview(prev => prev ? {
              ...prev,
              ScreeningCall: null
            } : null);
          }
        } catch (err) {
          console.error('Failed to delete phone call:', err);
          showErrorToast(err?.response?.data?.message || 'Failed to delete phone call');
        }
      }
    });
  };
  return {
    deleteInterviewRound,
    deletePanellist,
    handleDeleteInterviewRound,
    handleRemovePanellist,
    confirmRemoveInterviewRound,
    confirmRemovePanellist,
    handleDeletePhoneScreening
  };
}